import React from 'react';
import PropTypes from 'prop-types';
import Cookie from 'js-cookie';
import {CLOSE__16} from '@homeaway/svg-defs/lib/defs/CLOSE__16';
import {ButtonIcon} from '@homeaway/react-buttons';
import './TransitionBanner.less';
import {Logo, Vmark} from './svgs';
import triggerEdapEvent from '../../util/edap';
import brandNameLocaleMap from '../../util/config/alternateBrandCodeConfig';

class TransitionBanner extends React.Component {
    static propTypes = {
        /** Whether the banner is post or pre brand transition**/
        forPreTransition: PropTypes.bool,
        /** Title for the banner **/
        title: PropTypes.string,
        /** Info for the banner **/
        info: PropTypes.string,
        /** Whether to display banner based on cookie value, for ease of development **/
        useCookie: PropTypes.bool,
        /** Whether the cookie should be saved only after the user closes the banner **/
        useCookieOnClose: PropTypes.bool,
        /** Link to show in post transition banner **/
        link: PropTypes.string,
        /** Localized text**/
        learnMore: PropTypes.string,
        /** VGDC param if it exists **/
        vgdc: PropTypes.string,
        /** Locale for request site*/
        locale: PropTypes.string
    }

    static defaultProps = {
        forPreTransition: true,
        title: 'Title',
        info: 'Info',
        useCookie: true,
        useCookieOnClose: false,
        link: 'https://www.vrbo.com/l/hello-vrbo',
        learnMore: null,
        vgdc: null,
        locale: null
    }

    constructor(props) {
        super(props);
        this.state = {
            isOpen: false
        };
        this.closePopover = this.closePopover.bind(this);

        this.bannerType = this.props.forPreTransition ? 'Pre' : 'Post';
        this.cookieName = `seen${this.bannerType}TransBanner`;
        this.vgdcCookie = 'vgdc';

        // Get cookie irrespective of whether param exists or not
        this.vgdcCookieValue = Cookie.get(this.vgdcCookie) || null;
        this.alternateBrandName = this.props.vgdc || this.vgdcCookieValue;

        this.showBanner = this.props.useCookie ? !Cookie.get(this.cookieName) : true;
        if (!this.props.forPreTransition) {
            let showPostBanner = false;
            if (this.alternateBrandName && this.props.locale && this.alternateBrandName in brandNameLocaleMap) {
                const twoPartLocale = this.props.locale.substr(0, 5);
                showPostBanner = twoPartLocale === brandNameLocaleMap[this.alternateBrandName].locale;
            }
            this.showBanner = showPostBanner && this.showBanner;
        }
    }

    componentDidMount() {
        // Save cookie vgdc if param present
        if (this.props.vgdc && !this.props.forPreTransition) {
            Cookie.set(this.vgdcCookie, this.props.vgdc);
            this.handleGDPRTermsAccept(this.vgdcCookie, this.props.vgdc);
        }

        // Set cookie if component mounted, if useCookie is set
        if (this.props.useCookie && !this.props.useCookieOnClose) {
            Cookie.set(this.cookieName, 'true');
            this.handleGDPRTermsAccept(this.cookieName, 'true');
        }
        // Show after mounted, to prevent floating images before full render
        this.setState({isOpen: true});

        if (this.showBanner) {
            triggerEdapEvent({
                ...{eventaction: `${this.bannerType.toLocaleLowerCase()}branding pop up seen`},
                ...(!this.props.forPreTransition) && {eventlabel: this.props.vgdc || this.vgdcCookieValue}
            });
        }
    }

    handleGDPRTermsAccept(cookieName, value) {
        window.cookieconsent = window.cookieconsent || [];
        window.cookieconsent.push(function(cookieAccess) {
            cookieAccess.setCookie('functional', `${cookieName}=${value}; path=/`);
        });
    }

    closePopover() {
        this.setState({isOpen: false});
        if (this.props.useCookie && this.props.useCookieOnClose) {
            Cookie.set(this.cookieName, 'true');
            this.handleGDPRTermsAccept(this.cookieName, 'true');
        }
        // Remove vgdc cookie for post transition banner
        if (!this.forPreTransition) {
            Cookie.remove(this.vgdcCookie);
        }
        triggerEdapEvent({
            ...{eventaction: `${this.bannerType.toLocaleLowerCase()}branding pop up closed`},
            ...(!this.props.forPreTransition) && {eventlabel: this.props.vgdc || this.vgdcCookieValue}
        });
    }

    linkClicked(bannerType) {
        triggerEdapEvent({
            ...{eventaction: `${bannerType.toLocaleLowerCase()}branding learn more click`},
            ...(!this.props.forPreTransition) && {eventlabel: this.props.vgdc || this.vgdcCookieValue}
        });
    }

    getTitle() {
        if (!this.props.forPreTransition && this.alternateBrandName) {
            // if title value is present for the vgdc in brandNameLocaleMap then display it
            if (brandNameLocaleMap[this.alternateBrandName].title) {
                return brandNameLocaleMap[this.alternateBrandName].title;
            } else if (brandNameLocaleMap[this.alternateBrandName].name) {
                return this.props.title.replace('HomeAway', brandNameLocaleMap[this.alternateBrandName].name);
            }
        }
        return this.props.title;
    }

    getInfo() {
        if (!this.props.forPreTransition && this.alternateBrandName) {
            // if info value is present for the vgdc in brandNameLocaleMap then display it
            if (brandNameLocaleMap[this.alternateBrandName].info) {
                return brandNameLocaleMap[this.alternateBrandName].info;
            } else if (brandNameLocaleMap[this.alternateBrandName].name) {
                return this.props.info.replace('HomeAway', brandNameLocaleMap[this.alternateBrandName].name);
            }
        }
        return this.props.info;
    }

    /* eslint-disable complexity */
    render() {
        if (!this.showBanner) {
            return null;
        }
        return (
            this.state.isOpen &&
            <div>
                <div className="transition-banner__caret" />
                <div role="dialog" className={`transition-banner${ this.props.forPreTransition ? '' : ' transition-banner__post'}`}>
                    <Vmark />
                    <div className="transition-banner__content front">
                        {
                            this.props.forPreTransition && <Logo />
                        }
                        <div>
                            <ButtonIcon
                                label="close"
                                size="sm"
                                inverse
                                svgMarkup={CLOSE__16}
                                className="transition-banner__close front"
                                onClick={this.closePopover}
                            />
                        </div>
                        <div className={`transition-banner__title ${ this.props.forPreTransition ? '' : 'transition-banner__title__post'} front`}>
                            {this.getTitle()}
                        </div>
                        <div className={`transition-banner__info front ${ this.props.forPreTransition ? '' : 'transition-banner__info__post'} front`}>
                            <div>{this.getInfo()}</div>
                            {this.props.learnMore &&
                                <a href={this.props.link} className={'link'} target={'_blank'} onClick={() => this.linkClicked(this.bannerType)}>{this.props.learnMore}</a>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default TransitionBanner;

